import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Sitemark from '../../shared/components/SitemarkIcon';
import hips from './../../static/images/marketing/hanches3.png';
import { useNavigate } from 'react-router-dom';

const heroTitle = 'Revolutionizing \n medical imaging';
const heroSubtitle =
  'Transform MRI images into fully interactive 3D models of a patient’s anatomy with a detailed and precise representation of the nervous system.';

export default function Hero() {
  const navigate = useNavigate();
  const [isHeroImageLoaded, setIsHeroImageLoaded] = React.useState(false);

  const onButtonClick = React.useCallback(() => {
    navigate('/login');
  }, [navigate]);

  React.useEffect(() => {
    const image = new Image();
    image.src = hips;
    image.onload = () => setIsHeroImageLoaded(true);
  }, []);

  return (
    <Box
      id="hero"
      sx={{
        height: 'auto',
        minHeight: { md: '65vh' },
        width: '100%',
        backgroundColor: '#f5f2ef',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: { xs: 'center top', md: 'center' },
        backgroundSize: 'cover',
        backgroundImage: isHeroImageLoaded ? `url(${hips})` : 'none',
        py: { xs: 3, sm: 4, md: 0 },
      }}
    >
      <Container
        sx={{
          minHeight: { md: '65vh' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'row', sm: 'row' },
            gap: { xs: 1, sm: 3 },
            py: { xs: 1.5, sm: 4, md: 5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Sitemark sx={{ height: { xs: 28, sm: 36, md: 40 }, width: { xs: 120, sm: 160, md: 200 }, mr: 2, color: { xs: '#000000', sm: '#000000', md: '#000000', lg: '#ffffff' } }} />
          </Box>
          <Button
            sx={{
              borderColor: '#333333',
              minWidth: { xs: 90, sm: 120, md: 150 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 }
            }}
            variant="outlined"
            size="medium"
            onClick={onButtonClick}
          >
            Sign in
          </Button>
        </Box>
        <Stack
          spacing={2}
          useFlexGap
          sx={{
            justifyContent: 'center',
            alignItems: 'left',
            width: { xs: '100%', sm: '70%' },
            pb: { xs: 3, sm: 0 },
          }}
        >
          {isHeroImageLoaded ? (
            <>
              <Typography
                component="h1"
                variant="h1"
                sx={{
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  fontSize: { xs: '2.2rem', sm: 'clamp(3rem, 10vw, 3.5rem)' },
                  whiteSpace: 'pre-line',
                }}
              >
                {heroTitle}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'left',
                  color: 'text.secondary',
                  width: { sm: '100%', md: '80%' },
                  fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                }}
              >
                {heroSubtitle}
              </Typography>
            </>
          ) : (
            <>
              <Skeleton variant="text" width="80%" height={56} />
              <Skeleton variant="text" width="65%" height={56} />
              <Skeleton variant="text" width="90%" height={28} />
              <Skeleton variant="text" width="75%" height={28} />
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
